import { Injectable, OnDestroy, inject } from '@angular/core';
import { Actions, ofActionDispatched } from '@ngxs/store';
import { FeatureFlagPurePipe } from '@wilson/feature-flags';
import { Logout } from '@wilson/auth/core';
import { TabVisibilityService } from '@wilson/utils';
import {
  Observable,
  Subscription,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  merge,
  shareReplay,
  skip,
  take,
} from 'rxjs';
import { RealtimeClientAction, RealtimeServerEvent } from './realtime.types';
import { REALTIME_COLLABORATION_FEATURE_FLAG } from './realtime.config';
import { RealtimeSocketService } from './realtime-socket.service';
import { consoleInfo } from '@wilson/non-domain-specific/logger/logger';

type ReconnectHandler = () => void;
type SuspendHandler = () => void;

type ConnectionState =
  | 'idle'
  | 'pending'
  | 'suspended'
  | 'background'
  | 'active';

enum ReconcileEventType {
  ConsumerRegistered = 'consumer_registered',
  ConsumerRemoved = 'consumer_removed',
  LifecycleChanged = 'lifecycle_changed',
}

type ReconcileEvent =
  | { type: ReconcileEventType.ConsumerRegistered; consumerId: string }
  | { type: ReconcileEventType.ConsumerRemoved }
  | { type: ReconcileEventType.LifecycleChanged };

export interface RegisterConsumerOptions {
  consumerId: string;
  onReconnect: ReconnectHandler;
  onSuspend?: SuspendHandler;
}

interface ConsumerHandlers {
  onReconnect: ReconnectHandler;
  onSuspend?: SuspendHandler;
}

/**
 * This service is responsible for managing consumers of the websocket connection.
 * It ensures that the websocket connection is only active when there are consumers registered,
 * and that the connection is suspended when the feature flag is disabled or the tab is not visible.
 * It also handles the reconnection logic for the consumers when the connection is lost/user reenters the page.
 */
@Injectable({ providedIn: 'root' })
export class ConnectionConsumerManagerService implements OnDestroy {
  private readonly socket = inject(RealtimeSocketService);
  private readonly featureFlag$ = inject(FeatureFlagPurePipe).transform(
    REALTIME_COLLABORATION_FEATURE_FLAG,
  );
  private readonly tabVisibility = inject(TabVisibilityService);
  private readonly actions$ = inject(Actions);

  private readonly consumersMap = new Map<string, ConsumerHandlers>();
  private readonly lifecycleSubscription: Subscription;
  private readonly logoutSubscription: Subscription;
  private currentState: ConnectionState = 'idle';
  private featureEnabled: boolean | null = null;
  private tabVisible = true;

  readonly incomingActions$: Observable<RealtimeServerEvent> =
    this.socket.incomingActions$;

  readonly isEnabled$ = this.featureFlag$.pipe(
    map((flagEnabled) => this.socket.isConfigured && flagEnabled),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  constructor() {
    const tabVisible$ = this.tabVisibility.isTabVisible$.pipe(
      distinctUntilChanged(),
    );

    this.lifecycleSubscription = combineLatest([
      this.isEnabled$,
      merge(
        tabVisible$.pipe(take(1)),
        tabVisible$.pipe(skip(1), debounceTime(500)),
      ),
    ]).subscribe(([enabled, tabVisible]) => {
      this.featureEnabled = enabled;
      this.tabVisible = tabVisible;
      this.reconcile({ type: ReconcileEventType.LifecycleChanged });
    });

    this.logoutSubscription = this.actions$
      .pipe(ofActionDispatched(Logout))
      .subscribe(() => this.handleLogout());
  }

  ngOnDestroy(): void {
    this.lifecycleSubscription.unsubscribe();
    this.logoutSubscription.unsubscribe();
  }

  registerConsumer({
    consumerId,
    onReconnect,
    onSuspend,
  }: RegisterConsumerOptions): void {
    this.consumersMap.set(consumerId, { onReconnect, onSuspend });
    this.reconcile({ type: ReconcileEventType.ConsumerRegistered, consumerId });
  }

  removeConsumer(consumerId: string): void {
    this.consumersMap.delete(consumerId);
    this.reconcile({ type: ReconcileEventType.ConsumerRemoved });
  }

  send(action: RealtimeClientAction): void {
    this.socket.send(action);
  }

  private handleLogout(): void {
    this.log('logout detected, disconnecting socket and clearing consumers');
    this.notifySuspend();
    this.consumersMap.clear();
    this.socket.disconnect();
    this.currentState = 'idle';
  }

  private reconcile(event: ReconcileEvent): void {
    const next = this.deriveConnectionState();
    const prev = this.currentState;

    this.log(
      `state: ${JSON.stringify({ prev: prev, next: next, event: event })}`,
    );

    if (prev === next) {
      if (next === 'active') {
        if (event.type === ReconcileEventType.ConsumerRegistered) {
          this.log(
            `State has no changed and is active, new consumer registered, ensuring active connection`,
          );
          this.ensureActiveConnection(event.consumerId);
        } else {
          this.log(`State has no changed and is active, resuming keepalive`);
          this.socket.resumeKeepalive();
        }
      }
      this.currentState = next;
      return;
    }

    this.enterState(next, event);
    this.currentState = next;
  }

  private deriveConnectionState(): ConnectionState {
    if (this.consumersMap.size === 0) {
      return 'idle';
    }

    if (this.featureEnabled === null) {
      return 'pending';
    }

    if (!this.featureEnabled) {
      return 'suspended';
    }

    if (!this.tabVisible) {
      return 'background';
    }

    return 'active';
  }

  private enterState(state: ConnectionState, event: ReconcileEvent): void {
    switch (state) {
      case 'idle':
      case 'pending':
        this.log(`idle/pending state, disconnecting socket`);
        this.socket.disconnect();
        break;
      case 'suspended':
        this.log(`suspended state, disconnecting socket and notifying suspend`);
        this.socket.disconnect();
        this.notifySuspend();
        break;
      case 'background':
        this.log(`background state, pausing keepalive`);
        this.socket.pauseKeepalive();
        break;
      case 'active':
        this.log(
          `active state, resuming keepalive and ensuring active connection`,
        );
        this.socket.resumeKeepalive();
        this.ensureActiveConnection(
          event.type === ReconcileEventType.ConsumerRegistered
            ? event.consumerId
            : undefined,
        );
        break;
    }
  }

  private ensureActiveConnection(consumerId?: string): void {
    const notify = (): void => {
      if (consumerId) {
        this.log(`notify reconnect for consumer: ${consumerId}`);
        this.consumersMap.get(consumerId)?.onReconnect();
        return;
      }
      this.log(
        `notify reconnect for all consumers: ${JSON.stringify(Array.from(this.consumersMap.keys()))}`,
      );
      for (const consumer of this.consumersMap.values()) {
        consumer.onReconnect();
      }
    };

    if (this.socket.isOpen()) {
      this.log(`socket is open, notifying consumers`);
      notify();
    } else {
      this.log(`socket is closed, connecting and notifying consumers`);
      this.socket.connect(notify);
    }
  }

  private notifySuspend(): void {
    this.log(
      `notifying suspend for consumers: ${JSON.stringify(Array.from(this.consumersMap.keys()))}`,
    );
    for (const consumer of this.consumersMap.values()) {
      consumer.onSuspend?.();
    }
  }

  private log(message: string): void {
    consoleInfo(
      `[${new Date().toISOString()}][ConnectionConsumerManagerService] ${message}`,
    );
  }
}
